import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconArrowUpRight, IconX } from "@tabler/icons-react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import styles from "./FeatureCards.module.css";

/** Stable keys for each promo card, used for per-card dismissal. */
export const FEATURE_CARD_KEYS = ["pillbox", "maatraDesktop"] as const;

interface FeatureCardsProps {
  /** Keys of cards already dismissed (hidden). */
  dismissed: string[];
  /** Dismiss a single card by key (on X tap or card tap). */
  onDismiss: (key: string) => void;
  /**
   * Fill the available width instead of the default 90%. Used for inexperienced
   * users, whose dashboard lacks the centering widgets container.
   */
  fullWidth?: boolean;
}

const FeatureCards: React.FC<FeatureCardsProps> = ({
  dismissed,
  onDismiss,
  fullWidth = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showDesktopDrawer, setShowDesktopDrawer] = useState(false);

  const cards = [
    {
      key: "pillbox",
      title: t("dashboard.featureCards.pillbox.title"),
      description: t("dashboard.featureCards.pillbox.description"),
      cta: t("dashboard.featureCards.pillbox.cta"),
      onClick: () => navigate("/pillbox"),
      dismissOnClick: false,
    },
    {
      key: "maatraDesktop",
      title: t("dashboard.featureCards.maatraDesktop.title"),
      description: t("dashboard.featureCards.maatraDesktop.description"),
      cta: t("dashboard.featureCards.maatraDesktop.cta"),
      onClick: () => setShowDesktopDrawer(true),
      dismissOnClick: false,
    },
  ];

  const visibleCards = cards.filter((card) => !dismissed.includes(card.key));

  return (
    <>
      <div
        className={`${styles.featureCards} ${
          fullWidth ? styles.featureCardsFullWidth : ""
        }`}
      >
        {visibleCards.map((card) => (
          <div key={card.key} className={styles.featureCardSlot}>
            <button
              type="button"
              className={styles.featureCard}
              onClick={() => {
                card.onClick();
                if (card.dismissOnClick) onDismiss(card.key);
              }}
            >
              <span className={styles.featureCardTop}>
                <span className={styles.featureCardBadge}>
                  {t("dashboard.featureCards.justLaunched")}
                </span>
                <span className={styles.featureCardText}>
                  <span className={styles.featureCardTitle}>{card.title}</span>
                  <span className={styles.featureCardDescription}>
                    {card.description}
                  </span>
                </span>
              </span>
              <span className={styles.featureCardCta}>
                {card.cta}
                <IconArrowUpRight size={15} stroke={2} />
              </span>
            </button>
            <button
              type="button"
              className={styles.featureCardDismiss}
              aria-label={t("common.close")}
              onClick={() => onDismiss(card.key)}
            >
              <IconX size={20} stroke={2} color="#fff" />
            </button>
          </div>
        ))}
      </div>
      <FooterDrawer
        open={showDesktopDrawer}
        onClose={() => setShowDesktopDrawer(false)}
        title={t("dashboard.featureCards.maatraDesktop.drawerTitle")}
        height="30vh"
      >
        <div style={{ padding: "16px 24px 0" }}>
          <p
            className="body-base-medium"
            style={{ color: "var(--colour-text-default, #2A2A2A)" }}
          >
            {t("dashboard.featureCards.maatraDesktop.drawerBody1")}
          </p>
          <p
            className="body-base-medium"
            style={{
              marginTop: 22,
              color: "var(--colour-text-default, #2A2A2A)",
            }}
          >
            {t("dashboard.featureCards.maatraDesktop.drawerBody2")}
          </p>
        </div>
      </FooterDrawer>
    </>
  );
};

export default FeatureCards;
